import { cn } from "@/lib/utils";
import {
  accentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";

export interface ChoiceOption<T extends string = string> {
  key: T;
  label: string;
}

interface ChoicePillsProps<T extends string> {
  ariaLabel: string;
  options: ChoiceOption<T>[];
  value: T;
  onChange: (value: T) => void;
  // Colour family: the selected option is the solid colour, and the two-
  // option track is its light shade with text in its dark shade. Industry
  // pages pass their accent; the default is the Fydo red.
  accent?: IndustryAccent;
  // Centre the control in its row (default: start-aligned).
  isCentered?: boolean;
}

// The ONE single-choice control for 2.0 pages (owner's rule):
// - exactly two options -> a segmented toggle in the shadcn Tabs style: a
//   track in the light shade of the accent, two equal halves sized to the
//   longer label (labels wrap, balanced, only when the space runs out), and the
//   selected half filled (the "Without loyalty / With Fydo" switch);
// - three or more -> wrapping pill buttons.
// Buttons use aria-pressed inside a labelled group.
const ChoicePills = <T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  accent = "brand",
  isCentered = false,
}: ChoicePillsProps<T>) => {
  const isToggle = options.length === 2;
  const tone = accentClasses[accent];
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        isToggle
          ? cn(
              "grid w-fit max-w-full auto-cols-fr grid-flow-col gap-1 rounded-[24px] p-1",
              tone.soft,
            )
          : "flex flex-wrap gap-2",
        isCentered &&
          (isToggle
            ? "mx-auto self-center justify-self-center"
            : "justify-center"),
      )}
    >
      {options.map((option) => {
        const isOn = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={isOn}
            onClick={() => onChange(option.key)}
            className={cn(
              "cursor-pointer font-bold transition-colors duration-200 motion-reduce:transition-none",
              isToggle
                ? "min-h-10 min-w-0 rounded-[20px] px-4 py-2 text-[14.5px] leading-tight text-balance sm:px-5"
                : "rounded-full border-[1.5px] px-4 py-2 text-[14.5px]",
              isOn
                ? cn(
                    tone.solid,
                    "text-white",
                    isToggle ? "shadow-sm" : "border-transparent",
                  )
                : isToggle
                  ? cn(tone.text, "hover:bg-white/50")
                  : "border-line bg-white text-ink hover:border-line-warm",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default ChoicePills;
